import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import {
  InboundCancelRequest,
  InboundOrder,
  isCancelRequested,
  scheduleInboundRefetch,
  useInboundOrderMutations,
  useInboundOrderQuery,
} from 'src/services/inbound';
import { formValuesToInboundOrderUpdate, InboundFormValues } from 'src/pages/Inbound/InboundFormContext';
import { inboundErrorMessage } from 'src/utils/inboundErrors';

export type InboundCancelResult = 'cancelled' | 'requested' | false;

// Inbound-Draft spec 2026-11-03 §2.3/§6: the action flags come from the DTO (one
// source of truth, computed by the backend service); this hook only derives the
// UI state around them — edit mode, dialogs, toasts, error mapping.
export const useInboundOrder = (id: number, query: { offset: number; limit: number } = { offset: 0, limit: 10 }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: order, isLoading } = useInboundOrderQuery({ path: { inbound_order_id: id }, query });
  const mutations = useInboundOrderMutations();

  const [edit, setEdit] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [isExitDialogOpen, setExitDialogOpen] = useState(false);

  useEffect(() => {
    setEdit(false);
    setDirty(false);
    setExitDialogOpen(false);
  }, [id]);

  const key = order?.inbound_state?.key ?? undefined;
  const canEdit = !!order?.is_editable;
  const canConfirm = !!order?.is_editable && key === 'draft';
  const canCancel = !!order?.is_cancelable;
  const canDraft = key === 'cancel' && order?.cancel_request?.state !== 'pending';
  const cancelRequested = isCancelRequested(order);
  const rejectedRequest: InboundCancelRequest | null =
    order?.cancel_request?.state === 'rejected' ? order.cancel_request : null;

  const run = async <T>(action: () => Promise<T>, fallbackKey: string): Promise<T | false> => {
    setBusy(true);
    try {
      return await action();
    } catch (error) {
      toast.error(inboundErrorMessage(error, t, fallbackKey));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const leaveEdit = () => {
    setEdit(false);
    setDirty(false);
  };
  const startEdit = () => {
    setDirty(false);
    setEdit(true);
  };
  const requestExitEdit = () => {
    if (dirty) setExitDialogOpen(true);
    else leaveEdit();
  };
  const handleExitDialog = (confirmed: boolean) => {
    setExitDialogOpen(false);
    if (confirmed) leaveEdit();
  };

  const save = async (values: InboundFormValues) => {
    const result = await run(async () => {
      await mutations.updateInboundOrderAsync(id, formValuesToInboundOrderUpdate(values));
      toast.success(t('inbound.update_success'));
      leaveEdit();
      return true;
    }, 'inbound.update_failed');
    // the configurator resets on submit and reports "clean" — after a failed PUT
    // the buffer still differs from the server, keep the exit guard armed
    if (result !== true) setDirty(true);
    return result === true;
  };

  const confirm = async () => {
    const result = await run(async () => {
      await mutations.confirmInboundOrderAsync(id);
      toast.success(t('inbound.confirm_success'));
      return true;
    }, 'inbound.confirm_failed');
    return result === true;
  };

  const cancelOrder = async (): Promise<InboundCancelResult> =>
    run<InboundCancelResult>(async () => {
      const dto = await mutations.cancelInboundOrderAsync(id);
      if (isCancelRequested(dto)) {
        // Async saga at the warehouse: the state only flips once the WMS answers.
        toast.success(t('inbound.cancel_requested'));
        scheduleInboundRefetch(queryClient);
        return 'requested';
      }
      toast.success(t('inbound.cancel_success'));
      return 'cancelled';
    }, 'inbound.cancel_failed');

  const setToDraft = async () => {
    const result = await run(async () => {
      await mutations.setInboundOrderDraftAsync(id);
      toast.success(t('inbound.draft_success'));
      return true;
    }, 'inbound.draft_failed');
    return result === true;
  };

  const duplicate = async (): Promise<InboundOrder | undefined> => {
    const result = await run(() => mutations.duplicateInboundOrderAsync(id), 'inbound.duplicate_failed');
    return result === false ? undefined : result;
  };

  return {
    order,
    isLoading,
    edit,
    hasChanges: dirty,
    isBusy: busy,
    canEdit,
    canConfirm,
    canCancel,
    canDraft,
    isCancelRequested: cancelRequested,
    rejectedRequest,
    startEdit,
    requestExitEdit,
    isExitDialogOpen,
    handleExitDialog,
    setDirty,
    save,
    confirm,
    cancelOrder,
    setToDraft,
    duplicate,
  };
};
